import { collection, addDoc, onSnapshot, query, orderBy, limit } from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import { ExtractionJob } from "../types";

/**
 * Creates an extraction job doc in Firestore, triggering phq_onExtractionJobCreated.
 */
export async function startExtraction(
  opportunityId: string,
  documentPaths: string[],
  user: { uid: string; email: string },
  modelOverride?: string,
  locationOverride?: string,
): Promise<string> {
  const colRef = collection(db, "opportunities", opportunityId, "extraction_jobs");
  const now = new Date().toISOString();

  const docRef = await addDoc(colRef, {
    opportunityId,
    status: "queued",
    model: modelOverride || "gemini-3.8-flash",
    location: locationOverride || "global",
    documentPaths,
    requestedBy: user.uid,
    requestedByEmail: user.email,
    createdAt: now,
  });

  return docRef.id;
}

/**
 * Subscribes to the latest extraction job for an opportunity.
 */
export function subscribeToLatestExtractionJob(
  opportunityId: string,
  callback: (job: ExtractionJob | null) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "extraction_jobs");
  const q = query(colRef, orderBy("createdAt", "desc"), limit(1));

  return onSnapshot(
    q,
    (snapshot) => {
      const docSnap = snapshot.docs[0];
      if (!docSnap) {
        callback(null);
        return;
      }
      const data = docSnap.data();
      const job: ExtractionJob = {
        id: docSnap.id,
        opportunityId,
        status: data.status || "queued",
        model: data.model || "",
        location: data.location || "global",
        documentPaths: Array.isArray(data.documentPaths) ? data.documentPaths : [],
        counts: data.counts || undefined,
        error: data.error || undefined,
        requestedBy: data.requestedBy || "",
        requestedByEmail: data.requestedByEmail || "",
        createdAt: data.createdAt || new Date().toISOString(),
        startedAt: data.startedAt || undefined,
        completedAt: data.completedAt || undefined,
      };
      callback(job);
    },
    (error) => {
      console.error("Error subscribing to latest extraction job:", error);
      if (onError) onError(error);
    },
  );
}

export interface TestAiModelResponse {
  success: boolean;
  ok?: boolean;
  model?: string;
  location?: string;
  latencyMs?: number;
  error?: string;
  testedAt?: string;
}

/**
 * Calls phq_testAiModel Cloud Function to verify Gemini model connectivity.
 */
export async function testAiModel(
  model: string,
  location: string = "global",
): Promise<TestAiModelResponse> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User must be logged in to test AI models.");
  }

  const idToken = await user.getIdToken();
  const functionUrl =
    import.meta.env.VITE_TEST_AI_MODEL_FUNCTION_URL ||
    "https://us-central1-in-house-dev-mlc.cloudfunctions.net/phq_testAiModel";

  const response = await fetch(functionUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ model, location }),
  });

  const data = (await response.json()) as TestAiModelResponse;
  return data;
}
